import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuthStore } from '../../../stores/authStore';
import type { Message } from '../types';

const PAGE_SIZE = 50;

export function useThread(conversationId: string | null) {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const businessId = business?.id;
  const currentUserId = user?.id;
  const mountedRef = useRef(true);

  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [sending, setSending] = useState(false);
  const [showTranslations, setShowTranslations] = useState(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Fetch messages for active conversation
  const fetchMessages = useCallback(async () => {
    if (!conversationId || !currentUserId) { setMessages([]); return; }
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('messages')
        .select('*, sender:users!sender_id(first_name, last_name)')
        .eq('conversation_id', conversationId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true })
        .limit(PAGE_SIZE);
      if (err) throw err;
      if (!mountedRef.current) return;
      const msgs: Message[] = (data ?? []).map((m: Record<string, unknown>) => ({
        ...m,
        isSent: m.sender_id === currentUserId,
        senderName: m.sender
          ? `${(m.sender as Record<string, string>).first_name} ${(m.sender as Record<string, string>).last_name}`
          : 'Unknown',
      })) as Message[];
      setMessages(msgs);
      setHasMore((data ?? []).length === PAGE_SIZE);
    } catch { /* silent */ }
    finally { if (mountedRef.current) setLoading(false); }
  }, [conversationId, currentUserId]);

  useEffect(() => { void fetchMessages(); }, [fetchMessages]);

  // Realtime: listen for new messages in this conversation
  useEffect(() => {
    if (!conversationId) return;
    const ch = supabase
      .channel(`thread-${conversationId}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'messages',
        filter: `conversation_id=eq.${conversationId}`,
      }, (payload) => {
        const msg = payload.new as Record<string, unknown>;
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          // Replace optimistic message
          const optIdx = prev.findIndex(m => m.id.startsWith('opt-') && m.sender_id === msg.sender_id);
          const newMsg: Message = {
            ...msg,
            isSent: msg.sender_id === currentUserId,
            senderName: '',
            sender: null,
          } as unknown as Message;
          if (optIdx >= 0) {
            const next = [...prev];
            next[optIdx] = newMsg;
            return next;
          }
          return [...prev, newMsg];
        });
      })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [conversationId, currentUserId]);

  // Load more (older messages)
  const loadMore = useCallback(async () => {
    if (!conversationId || messages.length === 0 || !hasMore) return;
    const oldest = messages[0];
    const { data } = await supabase
      .from('messages')
      .select('*, sender:users!sender_id(first_name, last_name)')
      .eq('conversation_id', conversationId)
      .is('deleted_at', null)
      .lt('created_at', oldest.created_at)
      .order('created_at', { ascending: true })
      .limit(PAGE_SIZE);
    if (!mountedRef.current) return;
    const older: Message[] = (data ?? []).map((m: Record<string, unknown>) => ({
      ...m,
      isSent: m.sender_id === currentUserId,
      senderName: m.sender
        ? `${(m.sender as Record<string, string>).first_name} ${(m.sender as Record<string, string>).last_name}`
        : 'Unknown',
    })) as Message[];
    setMessages(prev => [...older, ...prev]);
    setHasMore((data ?? []).length === PAGE_SIZE);
  }, [conversationId, messages, hasMore, currentUserId]);

  // Send message
  const sendMessage = useCallback(async (content: string, referenceType?: string, referenceId?: string) => {
    if (!conversationId || !businessId || !currentUserId || !content.trim()) return;
    setSending(true);

    // Optimistic add
    const optId = `opt-${Date.now()}`;
    const optMsg: Message = {
      id: optId, conversation_id: conversationId, sender_id: currentUserId,
      content: content.trim(), message_type: 'direct', read_at: null,
      created_at: new Date().toISOString(), deleted_at: null,
      translations: null, original_language: null,
      reference_type: referenceType ?? null, reference_id: referenceId ?? null,
      reference_snapshot: null, sender: null, isSent: true, senderName: '',
    };
    setMessages(prev => [...prev, optMsg]);

    try {
      // Canonical send path: insert + conversation preview update + auto-unshelve, atomic.
      // The RPC derives sender + recipient itself, so no conversation pre-fetch is needed.
      const { data, error: err } = await supabase.rpc('send_message', {
        p_message_type: 'direct',
        p_content: content.trim(),
        p_conversation_id: conversationId,
        ...(referenceType && referenceId
          ? { p_reference_type: referenceType, p_reference_id: referenceId }
          : {}),
      });
      if (err) throw err;
      if (!data?.success) throw new Error(data?.error?.message ?? 'Failed to send');
      const realMsg = data.message as { id?: string } | null;

      // Fire-and-forget translation — on the REAL message id (was firing on the
      // optimistic `opt-…` id, which never resolved to a row).
      const autoTranslate = (business as unknown as Record<string, unknown>)?.auto_translate_messages;
      if (realMsg?.id && autoTranslate !== false && content.trim().length >= 5) {
        supabase.functions.invoke('translate-message', {
          body: { message_id: realMsg.id, text: content.trim(), sender_id: currentUserId, conversation_id: conversationId },
        }).catch(() => { /* silent */ });
      }

      // Activity log (no content)
      try {
        await supabase.from('activity_log').insert({
          business_id: businessId, activity_type: 'message_sent',
          description: 'Sent message from admin panel',
          entity_type: 'conversation', entity_id: conversationId,
          logged_by: currentUserId,
        });
      } catch { /* non-critical */ }
    } catch {
      // Remove optimistic message on failure
      setMessages(prev => prev.filter(m => m.id !== optId));
    } finally {
      if (mountedRef.current) setSending(false);
    }
  }, [conversationId, businessId, currentUserId, business]);

  const toggleTranslations = useCallback(() => setShowTranslations(p => !p), []);

  return {
    messages, loading, hasMore, loadMore,
    sendMessage, sending,
    showTranslations, toggleTranslations,
  };
}
